import type React from 'react';
import { useEffect, useState } from 'react';
import Logo from '../assets/logo.svg';
import { resolveAppBaseUrlFromHostname } from '../utils/structuredAiEnvironmentUrls';

type NotFoundProps = {
  variant?: 'page-not-found' | 'no-homepage' | 'no-site-found';
  className?: string;
  homeHref?: string;
};

const getSuffix = (variant?: NotFoundProps['variant']): string => {
  if (variant === 'no-homepage') return ' (No homepage found)';
  if (variant === 'no-site-found') return ' (No site found)';
  return ' (Page not found)';
};

const NotFound: React.FC<NotFoundProps> = ({
  variant,
  className,
  homeHref = '',
}) => {
  // Use state to ensure redirect URL is determined on client side only (avoiding SSR hydration mismatch)
  const [redirectHref, setRedirectHref] = useState<string>('');

  useEffect(() => {
    // Determine environment-specific redirect URL on client side only
    const envHref = resolveAppBaseUrlFromHostname(
      window.location.hostname.toLowerCase(),
    );

    const finalHref = homeHref && homeHref !== '/' ? homeHref : envHref;
    setRedirectHref(finalHref);
  }, [homeHref]);
  return (
    <div
      className={[
        'w-full min-h-[100vh] flex flex-col bg-white',
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="w-full bg-white border-b border-black/5">
        <div className="mx-auto w-full px-6 sm:px-10 py-4 flex items-center">
          <img
            src={Logo}
            alt="Logo"
            width={194}
            height={52}
            className="h-[52px] w-auto"
          />
        </div>
      </header>

      <main className="flex-1 w-full px-6 sm:px-10 flex items-center justify-center">
        <div className="w-full max-w-[625px] flex flex-col items-center gap-6 p-0">
          <div className="w-full flex justify-center">
            <div
              className="bg-white rounded-2xl p-12 flex flex-col items-center"
              role="img"
              aria-label="Not found illustration"
            >
              <div className="w-[251px] h-[212px] flex items-center justify-center">
                <svg
                  width="251"
                  height="212"
                  viewBox="0 0 251 212"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <g clipPath="url(#clip0_4740_177273)">
                    <path
                      d="M193.77 25.5092C217.13 43.7912 221.124 78.4322 214.851 107.425C209.198 133.547 186.118 149.304 163.801 164.011C140.207 179.559 114.836 199.649 87.9112 191.076C60.3453 182.299 47.5921 152.121 39.3566 124.389C31.6214 98.3418 28.2668 68.4074 45.1639 47.1287C60.8713 27.3481 89.9309 32.8595 114.926 29.2231C142.319 25.238 171.971 8.44913 193.77 25.5092Z"
                      fill="#EDEDED"
                    />
                    <mask
                      id="mask0_4740_177273"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="251"
                      height="212"
                    >
                      <path
                        d="M250.841 0.504028H0V211.401H250.841V0.504028Z"
                        fill="white"
                      />
                    </mask>
                    <g mask="url(#mask0_4740_177273)">
                      <path
                        d="M191.95 140.49L201.22 147.352C205.886 141.164 211.969 133.272 216.494 126.333C216.892 125.723 216.29 124.95 215.604 125.193C211.255 126.735 207.181 129.335 204.48 127.941C203.525 127.448 202.538 126.55 201.538 126.946C201.1 127.12 200.794 127.513 200.51 127.889C197.09 132.409 193.671 136.929 191.949 140.49H191.95Z"
                        fill="white"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M103.77 101.476C109.032 105.674 114.295 109.872 119.558 114.07C121.736 115.808 124.012 117.677 124.98 120.293C125.792 122.485 125.572 124.913 125.957 127.219C126.146 128.35 126.483 129.498 126.259 130.622C126.064 131.596 125.47 132.434 124.889 133.239C121.37 138.121 117.816 143.042 113.364 147.087C112.785 147.614 112.169 148.137 111.424 148.374C109.927 148.851 108.331 148.066 107.053 147.151C104.796 145.535 102.915 143.457 101.086 141.367C97.6275 137.415 94.2888 133.358 91.0456 129.227C88.7763 126.336 86.5486 123.402 84.6489 120.255C83.6127 118.538 82.996 116.818 82.4845 114.878C85.7668 113.092 102.566 102.315 103.77 101.476L103.77 101.476Z"
                        fill="white"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M160.597 199.531L160.843 211.077C168.583 210.994 178.535 210.993 186.782 210.34C187.507 210.283 187.75 209.333 187.139 208.938C183.262 206.432 178.719 204.79 178.168 201.797C177.974 200.738 178.08 199.406 177.156 198.856C176.751 198.615 176.254 198.612 175.783 198.616C170.123 198.668 164.462 198.72 160.597 199.531V199.531Z"
                        fill="white"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M103.354 155.352C103.354 155.352 131.971 122.757 149.343 103.627C151.799 100.922 154.485 98.0777 158.044 97.2639C161.87 96.3889 165.778 98.0691 169.285 99.8346C181.914 106.192 193.712 114.202 204.286 123.598C200.792 130.77 194.839 140.494 190.406 146.759C179.989 137.502 169.222 128.222 161.609 120.594C152.082 130.139 149.338 132.585 141.752 142.312C150.149 143.398 159.3 141.174 166.742 145.218C172.589 148.395 175.932 154.745 178.376 160.943C183.183 173.135 182.514 187.021 184.477 199.716C174.395 201.13 167.612 201.599 157.858 202.409C155.922 189.626 157.023 175.324 155.149 162.218C142.256 163.306 119.838 166.27 112.957 163.592C107.204 161.352 103.354 155.351 103.354 155.351L103.354 155.352Z"
                        fill="#010101"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M250.11 107.078C250.43 103.641 251.226 98.5092 249.044 95.8373C246.663 92.9207 242.089 92.5836 238.713 94.244C235.336 95.9049 233.454 98.9958 231.683 102.321C232.975 99.9948 233.277 97.4999 233.459 94.8443C233.698 91.3512 232.331 87.1639 228.94 86.3152C227.493 85.953 225.959 86.2923 224.564 86.8198C220.364 88.4066 216.947 91.6435 214.386 95.3369C211.826 99.0307 209.232 104.02 207.597 107.578C223.498 107.578 231.732 107.078 250.11 107.078Z"
                        fill="#CBE3FF"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M69.5739 182.717C66.1746 179.283 62.1857 175.259 58.7865 171.825C55.128 168.13 51.3067 164.327 46.4363 162.515C41.5659 160.704 35.3253 161.476 32.2879 165.699C29.3361 169.803 30.3353 175.584 32.5329 180.138C29.4285 175.591 24.3904 171.747 18.9179 172.282C18.1051 172.361 17.2864 172.542 16.5849 172.96C14.9955 173.908 14.3023 175.927 14.4183 177.777C14.5344 179.626 15.3149 181.36 17.5838 182.607C37.7527 182.607 45.2926 182.717 69.5743 182.717L69.5739 182.717Z"
                        fill="#CBE3FF"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M94.8381 103.887C96.6321 103.016 98.4613 101.902 99.3209 100.1C99.8409 99.0106 99.9509 97.7765 100.04 96.5721C100.226 94.0556 100.34 91.44 99.4008 89.0988C98.4827 86.8105 96.6355 85.0084 94.6017 83.6197C92.5679 82.2305 90.3201 81.1923 88.1849 79.9655C87.4108 79.5207 86.6277 79.0289 86.1223 78.2922C85.6469 77.5997 85.4587 76.7514 85.0671 76.0078C84.3193 74.588 82.8584 73.6424 81.3125 73.2208C79.7665 72.7997 78.1316 72.8484 76.5379 73.018C75.2141 73.1593 73.8779 73.3815 72.5601 73.1928C71.45 73.0344 70.4034 72.5908 69.3242 72.2851C67.9502 71.8958 66.5129 71.7301 65.0864 71.7959C63.8607 71.8528 62.6057 72.0948 61.5866 72.7795C59.9362 73.8883 59.2 75.932 58.7792 77.8779C58.1091 80.9757 57.9819 84.2256 58.728 87.3062C59.4737 90.3868 61.1323 93.2887 63.6139 95.2553C64.9167 96.2874 66.4338 97.0621 67.5698 98.2764C70.5147 101.425 70.342 106.884 73.8327 109.412C75.8296 110.858 78.5605 110.9 80.9235 110.202C83.2865 109.504 85.3955 108.159 87.5526 106.967C89.7098 105.775 92.206 105.163 94.8385 103.886L94.8381 103.887Z"
                        fill="#010101"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M82.4656 98.0673C81.8132 97.8937 81.086 98.1133 80.5926 98.575C80.0992 99.0366 79.834 99.7156 79.8172 100.392C79.7888 101.553 80.5139 102.697 81.5742 103.166C82.6349 103.634 83.9669 103.398 84.6971 102.486C86.2903 104.97 88.2867 108.005 89.7906 110.422C89.176 110.987 88.3572 111.413 87.0244 112.15C91.6065 114.301 101.59 117.118 101.59 117.118C101.59 117.118 101.031 108.13 100.657 103.518C99.4863 104.305 98.8317 104.708 97.8626 105.3C97.0266 103.948 95.8477 102.398 94.8458 100.984C96.3879 99.9951 97.2634 98.0574 96.9879 96.2441C96.8384 95.2614 96.3879 94.3519 95.8997 93.4864C94.2957 90.6431 92.2125 88.0715 89.7566 85.769C87.7658 86.5854 85.4351 86.5273 83.3978 85.6837C84.4697 89.1613 84.8853 95.8035 82.4656 98.0673Z"
                        fill="white"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M90.4362 91.1263C91.3065 91.9032 92.2065 92.646 93.1335 93.3535C93.1671 93.3789 93.2027 93.4086 93.21 93.4499C93.2161 93.4848 93.1997 93.5188 93.183 93.5503C92.9002 94.0859 92.4283 94.5187 91.8712 94.7543"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M109.769 96.2332C107.361 98.1494 104.953 100.066 102.367 102.293C108.592 109.376 114.382 116.843 119.694 124.636C121.292 126.981 122.936 129.441 125.399 130.845C126.017 131.197 126.692 131.479 127.401 131.518C128.261 131.565 129.102 131.251 129.867 130.856C132.027 129.74 133.887 127.763 134.232 125.353C134.272 125.073 134.29 124.781 134.201 124.511C134.094 124.188 133.845 123.934 133.604 123.693C128.585 118.666 123.817 113.388 119.308 107.588C124.527 96.8903 129.983 87.1365 135.202 76.4384C136.183 74.4278 137.182 72.3527 137.332 70.12C137.647 65.4207 134.226 61.3855 130.952 58.0056C124.032 50.8635 114.777 42.274 107.299 35.8734C102.301 40.5361 98.1772 45.5312 97.1018 46.0083C106.451 55.375 113.243 62.3268 121.034 69.2829C117.442 77.319 113.379 86.7713 109.769 96.2332Z"
                        fill="#E3E3E3"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M40.2349 80.0159L48.8329 114.191C48.8329 114.191 68.7728 116.823 77.615 117.69C80.532 116.034 83.4494 114.378 86.3591 112.453C92.0969 121.548 97.8884 130.713 105.328 138.472C107.295 140.524 109.413 142.527 110.642 145.093C113.007 150.03 111.218 156.581 106.313 159.376C100.712 150.315 90.3622 140.087 83.5719 131.524C72.8821 131.868 44.0278 130.385 39.359 127.038C37.1399 125.447 35.952 122.743 34.9729 120.191C31.7963 111.911 29.1367 99.4606 24.6957 84.5851C24.5633 84.1416 40.2349 80.0159 40.2349 80.0159Z"
                        fill="#E3E3E3"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M101.59 117.117C106.909 124.235 112.429 131.194 118.13 138.01"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M91.6064 102.524C92.8215 102.028 93.9965 101.433 95.1157 100.748"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M133.165 142.634C136.293 142.464 139.427 142.412 142.558 142.478L133.165 142.634Z"
                        fill="white"
                      />
                      <path
                        d="M133.165 142.634C136.293 142.464 139.427 142.412 142.558 142.478"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M167.209 0.824951C167.102 22.4055 166.995 43.9861 166.888 65.5666"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M191.95 39.6621V91.1263"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20.4686 72.4253C20.6444 72.2195 20.6379 71.6071 20.5911 71.3401C20.5443 71.0732 20.4256 70.8256 20.3229 70.5749C20.1342 70.1163 19.9945 69.6379 19.9064 69.1496C19.8231 68.6871 19.8355 68.1114 20.2387 67.8711C20.5167 67.7053 20.8864 67.7794 21.1524 67.9641C21.4185 68.1489 21.6041 68.4253 21.7872 68.6927C22.1826 69.2693 22.6055 69.8425 23.1669 70.2584C23.2919 70.351 23.4376 70.4617 23.4355 70.6176C23.4346 70.6869 23.4028 70.7515 23.3702 70.8126C23.1286 71.2656 22.799 71.6713 22.4057 72.0003C22.1216 72.238 21.7967 72.4391 21.4348 72.5166C21.0729 72.5941 20.7119 72.8503 20.469 72.4249L20.4686 72.4253Z"
                        fill="white"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M104.409 44.9016C77.0016 61.1038 59.5085 70.5807 30.8528 84.4468C25.5475 75.8372 19.0898 65.6261 14.405 56.3735C25.0257 49.2434 39.046 43.7574 49.7616 37.9958C66.5043 28.9939 70.6896 26.48 87.5999 18.1033C92.9556 27.1944 98.3108 36.2854 104.409 44.9016Z"
                        fill="black"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M57.1472 44.8221C56.6547 44.863 56.218 45.1916 55.949 45.6067C55.6799 46.0218 55.5587 46.5166 55.4917 47.007C55.2952 48.4444 55.5273 49.9064 55.86 51.3183C56.1394 52.5038 56.4961 53.6863 57.1171 54.7331C57.7382 55.7799 58.6489 56.6906 59.7857 57.1217C60.9225 57.5527 62.2957 57.4434 63.2421 56.6786C63.9801 56.0818 64.397 55.1512 64.5225 54.2095C64.648 53.2677 64.507 52.3092 64.2878 51.3846C63.8684 49.6157 63.1394 47.882 61.9119 46.5428C60.6844 45.2036 58.9786 44.6701 57.1472 44.8221Z"
                        fill="black"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21.4752 71.4485C22.9313 70.4732 24.4644 69.6753 26.046 69.0694C26.3435 68.9557 26.6529 68.8472 26.9619 68.895C27.271 68.9428 27.5808 69.1865 27.6569 69.5599C27.7781 70.1567 27.2899 70.6532 26.8562 70.9542C25.6489 71.7922 24.3703 72.473 23.0482 72.9811C25.2367 72.3468 27.2924 70.1364 28.6992 70.7173C29.2162 70.9309 29.2046 72.0962 27.9861 72.8472C26.7406 73.6149 25.535 74.1739 24.2912 74.9408C25.3764 74.3513 26.4612 73.7622 27.5465 73.1727C27.8774 72.9931 28.2204 72.8101 28.5818 72.8097C28.9433 72.8097 29.331 73.0418 29.4487 73.4642C29.5583 73.8569 29.4049 74.3031 29.1599 74.5873C28.9149 74.8715 28.593 75.0273 28.2797 75.1772C27.1536 75.7155 26.028 76.2537 24.9019 76.792C26.0151 76.2641 27.1351 75.7335 28.3059 75.4545C28.7022 75.3598 29.2252 75.3757 29.3722 75.8403C29.4969 76.2339 29.2441 76.6477 28.9914 76.9324C28.9914 76.9324 27.9298 77.6524 27.2783 78.1002C27.2288 78.1342 29.8691 82.8503 29.8691 82.8503L28.3046 83.3067C28.3046 83.3067 27.5924 82.3056 25.2995 81.083C24.2138 80.5043 23.1299 79.7369 22.514 78.7831C21.5229 77.248 20.7076 75.4748 20.3427 73.5447C20.2146 72.8661 20.2687 72.2559 21.4752 71.4477V71.4485Z"
                        fill="white"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M27.4235 78.0405C26.8626 78.2692 26.3134 78.5753 25.8298 78.9556L27.4235 78.0405Z"
                        fill="white"
                      />
                      <path
                        d="M27.4235 78.0405C26.8626 78.2692 26.3134 78.5753 25.8298 78.9556"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M97.0343 27.7494C96.7743 27.8235 96.2216 27.5613 95.9994 27.4075C95.7772 27.2538 95.6018 27.0419 95.4179 26.8434C95.0809 26.48 94.7053 26.1527 94.2996 25.869C93.9153 25.5998 93.388 25.3707 93.0016 25.6369C92.7351 25.8207 92.6479 26.188 92.7042 26.5071C92.7605 26.8262 92.9337 27.1108 93.0996 27.3894C93.4572 27.9901 93.8001 28.6145 93.9428 29.2992C93.9746 29.4516 94.0142 29.6304 94.1564 29.6937C94.2196 29.7216 94.2918 29.7199 94.3606 29.7156C94.8721 29.685 95.3775 29.5546 95.8404 29.3341C96.1748 29.1748 96.4928 28.9633 96.7142 28.6662C96.9355 28.3691 97.3189 28.1478 97.0343 27.7494Z"
                        fill="white"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M95.7281 28.2566C94.2355 29.1738 92.8713 30.2357 91.6614 31.4216C91.4341 31.6446 91.2063 31.881 91.1207 32.182C91.0352 32.483 91.1263 32.8671 91.4332 33.0924C91.9236 33.4519 92.5782 33.2151 93.032 32.9464C94.2965 32.198 95.4479 31.3191 96.4609 30.3283C94.9717 32.0546 92.1084 33.0015 92.0478 34.5246C92.0255 35.0844 93.087 35.5606 94.2767 34.7657C95.493 33.9532 96.5035 33.0898 97.7185 32.2781C96.7308 33.0192 95.7432 33.7602 94.7555 34.5013C94.4542 34.7274 94.1452 34.9629 93.9939 35.2915C93.8426 35.6201 93.8916 36.07 94.2256 36.3538C94.5359 36.6178 95.0048 36.6647 95.3645 36.5601C95.7243 36.4554 96.0006 36.2281 96.2671 36.0059C97.2255 35.2062 98.184 34.4066 99.1424 33.6074C98.1986 34.3997 97.25 35.1972 96.5078 36.1458C96.2564 36.4671 96.0522 36.9494 96.4124 37.2775C96.7171 37.5552 97.198 37.4984 97.5616 37.3873C97.5616 37.3873 98.199 37.0893 98.8772 36.6832C98.9288 36.6527 101.893 41.4411 101.893 41.4411L103.629 39.4262C103.629 39.4262 103.019 38.3596 102.868 35.7626C102.797 34.5328 102.554 33.2258 101.946 32.2664C100.968 30.7231 99.7007 29.2401 98.1028 28.102C97.541 27.7015 96.9647 27.4966 95.7281 28.2566Z"
                        fill="white"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M98.6525 36.8135C99.0861 36.494 99.9221 35.8704 100.715 35.3541L98.6525 36.8135Z"
                        fill="white"
                      />
                      <path
                        d="M98.6525 36.8135C99.0861 36.494 99.9221 35.8704 100.715 35.3541"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M220.728 47.5789C220.185 47.7498 219.915 48.4707 220.178 48.9766C220.441 49.4826 221.143 49.6837 221.652 49.4292C222.158 49.1769 222.421 48.5017 222.18 47.9897C221.939 47.4777 221.274 47.407 220.728 47.5789Z"
                        fill="#010101"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11.7222 136.273C11.179 136.444 10.9086 137.165 11.1717 137.671C11.4347 138.177 12.1366 138.378 12.6463 138.123C13.1522 137.871 13.4152 137.196 13.1741 136.684C12.933 136.172 12.2676 136.101 11.7222 136.273Z"
                        fill="#010101"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M218.608 171.22C218.065 171.391 217.795 172.112 218.058 172.618C218.321 173.124 219.023 173.325 219.532 173.07C220.038 172.818 220.301 172.143 220.06 171.631C219.819 171.119 219.154 171.048 218.608 171.22Z"
                        fill="#010101"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M0.322388 94.2308C1.84945 94.5805 3.37651 94.9302 4.90357 95.2798"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M150.01 33.0051C149.423 34.0636 148.836 35.1224 148.25 36.1809"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M137.286 186.299C137.324 187.547 137.792 188.796 137.83 190.043"
                        stroke="#010101"
                        strokeWidth="0.968699"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <path
                      d="M47.9236 67.9492L45.6079 63.9384L45.4486 63.4674L40.4394 54.7912L41.3756 54.5603L39.9909 66.7873L38.6501 66.4637L49.0784 60.4429L50.1387 62.2795L38.5283 68.9828L37.541 67.2729L39.2328 52.7013L41.4493 51.4216L50.2246 66.6208L47.9236 67.9492ZM62.7103 59.6936C61.4859 60.4005 60.2184 60.6914 58.9076 60.5662C57.5888 60.4269 56.3277 59.9165 55.1245 59.035C53.9132 58.1394 52.8647 56.9246 51.979 55.3906C51.0934 53.8566 50.5778 52.3623 50.4322 50.9077C50.2785 49.439 50.4792 48.1128 51.0343 46.9291C51.5812 45.7314 52.4669 44.7791 53.6913 44.0722C54.9157 43.3653 56.1762 43.0786 57.4728 43.2119C58.7754 43.323 60.0243 43.8122 61.2194 44.6797C62.4064 45.5331 63.4427 46.7268 64.3283 48.2607C65.2221 49.8088 65.7499 51.3242 65.9117 52.807C66.0735 54.2898 65.885 55.6371 65.3462 56.8489C64.7992 58.0466 63.9206 58.9948 62.7103 59.6936ZM61.6012 57.7726C62.4034 57.3095 62.9437 56.6691 63.2223 55.8515C63.4927 55.0199 63.5127 54.0795 63.2823 53.0304C63.0578 51.9591 62.6003 50.8253 61.9096 49.629C61.2271 48.4469 60.4862 47.5049 59.6869 46.8029C58.9016 46.0929 58.0935 45.6682 57.2624 45.5288C56.4373 45.3672 55.6166 45.522 54.8004 45.9932C53.9701 46.4726 53.4116 47.1141 53.1249 47.9176C52.8523 48.713 52.8201 49.6322 53.0283 50.6754C53.2425 51.6964 53.6908 52.7979 54.3734 53.9801C55.064 55.1763 55.8171 56.1395 56.6327 56.8695C57.4482 57.5996 58.2726 58.0525 59.1058 58.2282C59.9532 58.3957 60.7849 58.2439 61.6012 57.7726ZM80.7042 49.0233L78.3885 45.0125L78.2292 44.5415L73.22 35.8653L74.1562 35.6344L72.7715 47.8614L71.4307 47.5378L81.859 41.517L82.9193 43.3536L71.3089 50.0569L70.3217 48.347L72.0134 33.7754L74.23 32.4957L83.0052 47.6949L80.7042 49.0233Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4740_177273">
                      <rect
                        width="251"
                        height="211"
                        fill="white"
                        transform="translate(0 0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
          </div>

          <div
            className="w-full flex flex-col items-center gap-2"
            style={{ letterSpacing: '-0.03em' }}
          >
            <h1 className="text-[16px] leading-[1.22] text-center text-[#010506]">
              Sorry, the page you are looking for doesn't exist or has been
              removed{getSuffix(variant)}.
            </h1>
          </div>

          <div className="w-full flex items-center justify-center">
            <a
              href={redirectHref}
              className="h-10 px-4 inline-flex items-center justify-center rounded-full border border-[rgba(1,5,6,0.1)] text-[#010506] text-[16px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black/30"
              aria-label="Home"
            >
              Home
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotFound;
